/**
 * marginMockData.ts — Datos demo de rentabilidad (margen bruto).
 *
 * QUÉ HACE: genera, de forma DETERMINISTA (sin Math.random), el margen bruto por
 *   periodo para TODO el inventario demo (`defaultProducts`), asignando una
 *   cantidad vendida estable por producto (hash del id) para que todos tengan un
 *   margen comparable. Sirve de respaldo cuando el negocio aún no tiene
 *   actividad real de caja (modo demo).
 *
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 *
 * Exporta: MARGIN_MOCK_DATA
 */
import { defaultProducts } from "@/mocks/data/products";
import type {
  MarginData,
  MarginPeriod,
  MarginProduct,
} from "../types/metrics.types";

const CATEGORY_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#a855f7",
  "#ef4444",
  "#06b6d4",
];

/** Multiplicador de unidades según periodo (mes = base). */
const PERIOD_MULTIPLIER: Record<MarginPeriod, number> = {
  hoy: 1 / 30,
  semana: 1 / 4.3,
  mes: 1,
  anio: 12,
};

/** Hash determinista (FNV-1a) para variar la cantidad por producto. */
function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function buildMarginData(period: MarginPeriod): MarginData {
  const multiplier = PERIOD_MULTIPLIER[period];
  const productos: MarginProduct[] = [];
  const catAcc = new Map<string, number>();
  let ingresos = 0;
  let costo = 0;

  defaultProducts.forEach((product) => {
    const baseQty = 1 + (hash(product.id) % 40);
    const cantidadVendida = Math.max(1, Math.round(baseQty * multiplier));
    const totalIngresos = cantidadVendida * (Number(product.precio) || 0);
    const costoProducto = cantidadVendida * (Number(product.costo) || 0);
    const margen = totalIngresos - costoProducto;
    const categoria = product.categoria || "Otros";

    productos.push({
      id: product.id,
      nombre: product.nombre,
      categoria,
      cantidadVendida,
      totalIngresos,
      costo: costoProducto,
      margen,
    });

    ingresos += totalIngresos;
    costo += costoProducto;
    catAcc.set(categoria, (catAcc.get(categoria) ?? 0) + margen);
  });

  const margen = ingresos - costo;
  const porCategoria = Array.from(catAcc.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, value], idx) => ({
      name,
      value,
      color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length],
    }));

  return {
    ingresos,
    costo,
    margen,
    margenPct: ingresos > 0 ? (margen / ingresos) * 100 : 0,
    porCategoria,
    topProductos: [...productos].sort((a, b) => b.margen - a.margen),
  };
}

/** Datos demo de margen por periodo (todo el inventario demo). */
export const MARGIN_MOCK_DATA: Record<MarginPeriod, MarginData> = {
  hoy: buildMarginData("hoy"),
  semana: buildMarginData("semana"),
  mes: buildMarginData("mes"),
  anio: buildMarginData("anio"),
};
